import React, { useState } from "react";
import { FaUserShield, FaLock, FaEye, FaEyeSlash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import LoginAnimation from "../components/LoginAnimation";
import Background3D from "../components/ui/Background3D";
import { useTheme } from "../context/ThemeContext";
import ThemeCustomizer from "../components/ui/ThemeCustomizer";

const AdminLoginPage = () => {
  const [adminID, setAdminID] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [showAnimation, setShowAnimation] = useState(false);
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/admin-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ adminID, password }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Admin login failed.");
      }

      const data = await response.json();
      localStorage.setItem("adminToken", data.token);
      localStorage.setItem("adminID", data.adminID);
      setMessage("Admin login successful! Redirecting...");
      setShowAnimation(true);
      setTimeout(() => navigate("/dashboard"), 1500);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 relative overflow-hidden transition-colors duration-300 ${isDarkMode ? 'dark bg-dark-950' : 'bg-mesh'}`}>
      {showAnimation && <LoginAnimation />}
      <Background3D variant="auth" />

      <div className="absolute top-4 right-4 z-20">
        <ThemeCustomizer variant="auth" />
      </div>

      <div className="relative w-full max-w-md z-10 animate-fade-in-up">
        <div className="glass-strong rounded-3xl shadow-3d p-8 md:p-10 border border-white/40 dark:border-dark-600">
          <div className="flex justify-center mb-8">
            <div className="relative">
              <div className="w-20 h-20 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-2xl flex items-center justify-center shadow-glow-lg transform hover:rotate-3 transition-transform duration-500">
                <FaUserShield className="w-10 h-10 text-white" />
              </div>
              <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-emerald-500 rounded-lg border-4 border-white dark:border-dark-800 shadow-sm" />
            </div>
          </div>

          <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-2">
            Admin <span className="text-gradient">Login</span>
          </h2>
          <p className="text-center text-gray-500 dark:text-gray-400 mb-8">
            Welcome back! Please sign in to continue.
          </p>

          <form onSubmit={handleAdminLogin} className="space-y-5">
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <FaUserShield className="w-5 h-5 text-gray-400 group-focus-within:text-primary-500 transition-colors" />
              </div>
              <input
                type="text"
                className="input-modern pl-12"
                placeholder="Admin ID"
                value={adminID}
                onChange={(e) => setAdminID(e.target.value)}
                required
              />
            </div>

            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <FaLock className="w-5 h-5 text-gray-400 group-focus-within:text-primary-500 transition-colors" />
              </div>
              <input
                type={showPassword ? "text" : "password"}
                className="input-modern pl-12 pr-12"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <FaEyeSlash className="w-5 h-5" /> : <FaEye className="w-5 h-5" />}
              </button>
            </div>

            <div className="text-right">
              <a href="/forgetpassword" className="text-sm text-primary-600 dark:text-primary-400 hover:text-secondary-500 font-medium transition-colors">
                Forgot Password?
              </a>
            </div>

            <button type="submit" disabled={loading} className="btn-premium w-full py-4">
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="spinner w-5 h-5 border-2" />
                  Logging in...
                </span>
              ) : (
                "Sign In"
              )}
            </button>
          </form>

          {message && (
            <div className={`mt-6 p-4 rounded-xl text-center text-sm font-medium ${
              message.includes("successful")
                ? "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800"
                : "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800"
            }`}>
              {message}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
